import { getAccessToken, signOut } from './session';
import type {
  PagespeedSummary,
  Prospect,
  ProspectPatch,
  QuotaStatusResponse,
  SearchRequest,
  SearchResponse,
  SectorsResponse,
  WebsiteQuality,
} from '../types';

/**
 * Adresse du backend.
 *
 * Vide en developpement : le frontend appelle la meme origine et Vite proxifie
 * /api vers `DEV_API_TARGET`.
 *
 * **En production, elle est renseignee** et pointe sur le service Render. Le
 * frontend est publie sur un hebergeur statique, donc sur une autre origine
 * que l'API : c'est ce qui permet d'afficher l'interface instantanement pendant
 * que Render se reveille, au lieu d'une page blanche jusqu'a une minute. Le
 * backend doit alors autoriser cette origine (ALLOWED_ORIGINS).
 */
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

const apiUrl = (path: string): string => `${API_BASE_URL}${path}`;

/**
 * En-tete d'authentification pose sur chaque appel.
 *
 * Le jeton vient de la session Supabase et il est relu **a chaque requete** :
 * le SDK le renouvelle en tache de fond, et un jeton garde de cote serait
 * perime au bout d'une heure.
 *
 * Absence de jeton = personne n'est connecte. On echoue ici plutot que
 * d'envoyer une requete dont on sait qu'elle reviendra en 401.
 */
async function authHeaders(): Promise<Record<string, string>> {
  const token = await getAccessToken();
  if (token === null) throw new ApiError('UNAUTHORIZED', 'Session expiree. Reconnecte-toi.');
  return { Authorization: `Bearer ${token}` };
}

export class ApiError extends Error {
  readonly code: string;
  readonly fields?: Record<string, string>;

  constructor(code: string, message: string, fields?: Record<string, string>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.fields = fields;
  }
}

interface ApiErrorBody {
  error?: { code?: string; message?: string; fields?: Record<string, string> };
}

const GENERIC_MESSAGE = 'Impossible de contacter le serveur. Verifie qu il est bien demarre.';

/**
 * Reveil de l'hebergeur : dire ce qui se passe plutot que de laisser l'ecran
 * muet.
 *
 * L'offre gratuite de Render endort le service apres 15 minutes d'inactivite.
 * Le premier appel qui suit prend alors 23 a 60 secondes, mesure. Rien n'est
 * casse et il n'y a rien a faire — mais sans retour a l'ecran, une minute
 * d'attente ressemble a une panne.
 *
 * Ce n'est volontairement pas un delai d'expiration : couper la requete
 * n'aiderait personne, puisqu'elle finit par aboutir. On se contente de
 * prevenir au-dela de trois secondes.
 */
const WAKE_UP_DELAY_MS = 3_000;

export const WAKE_UP_MESSAGE = 'Reveil du serveur en cours, jusqu a une minute...';

type WakeUpListener = (reveilEnCours: boolean) => void;

const wakeUpListeners = new Set<WakeUpListener>();

/** Nombre de requetes actuellement au-dela du seuil : gere les appels paralleles. */
let slowRequests = 0;

function notifyWakeUp(): void {
  const reveilEnCours = slowRequests > 0;
  for (const listener of wakeUpListeners) listener(reveilEnCours);
}

/** S'abonner a l'etat « le serveur met anormalement longtemps a repondre ». */
export function onWakeUp(listener: WakeUpListener): () => void {
  wakeUpListeners.add(listener);
  return () => {
    wakeUpListeners.delete(listener);
  };
}

/**
 * Entoure un appel reseau : signale le reveil au-dela du seuil, et retire le
 * signal quoi qu'il arrive — succes, erreur ou abandon.
 */
async function withWakeUpNotice<T>(run: () => Promise<T>): Promise<T> {
  let signale = false;

  const timer = setTimeout(() => {
    signale = true;
    slowRequests += 1;
    notifyWakeUp();
  }, WAKE_UP_DELAY_MS);

  try {
    return await run();
  } finally {
    clearTimeout(timer);
    if (signale) {
      slowRequests -= 1;
      notifyWakeUp();
    }
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Appelle le backend (jamais Google directement : la cle reste cote serveur)
 * et convertit toute erreur en ApiError exploitable par l'UI.
 */
async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  // Resolu avant le try : une session absente doit remonter telle quelle, et
  // non etre confondue avec une panne reseau par le catch ci-dessous.
  const auth = await authHeaders();
  let response: Response;

  try {
    response = await withWakeUpNotice(() =>
      fetch(apiUrl(path), {
        method: options.method ?? 'GET',
        ...(options.body === undefined
          ? { headers: auth }
          : {
              headers: { 'Content-Type': 'application/json', ...auth },
              body: JSON.stringify(options.body),
            }),
        signal: options.signal,
      }),
    );
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError('NETWORK_ERROR', GENERIC_MESSAGE);
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (!response.ok) {
    const parsed = (body ?? {}) as ApiErrorBody;

    // 401 : le serveur ne reconnait plus la session — jeton expire que le SDK
    // n'a pas pu renouveler, compte revoque, ou projet Supabase change. On la
    // ferme ici, et l'abonnement d'App.tsx ramene de lui-meme a la connexion.
    //
    // 403 (OWNER_ALREADY_CLAIMED) ne doit surtout pas passer par la : le compte
    // est authentique, il n'y a rien a deconnecter, c'est la bibliotheque qui
    // appartient a quelqu'un d'autre.
    if (response.status === 401) void signOut();

    throw new ApiError(
      parsed.error?.code ?? 'UNKNOWN_ERROR',
      parsed.error?.message ?? GENERIC_MESSAGE,
      parsed.error?.fields,
    );
  }

  return body as T;
}

export function searchBusinesses(
  values: SearchRequest,
  signal?: AbortSignal,
): Promise<SearchResponse> {
  return request<SearchResponse>('/api/search', { method: 'POST', body: values, signal });
}

export async function getSectors(signal?: AbortSignal): Promise<SectorsResponse> {
  return request<SectorsResponse>('/api/sectors', { signal });
}

export function getQuotaStatus(signal?: AbortSignal): Promise<QuotaStatusResponse> {
  return request<QuotaStatusResponse>('/api/quota-status', { signal });
}

export async function listProspects(signal?: AbortSignal): Promise<Prospect[]> {
  const body = await request<{ prospects: Prospect[] }>('/api/prospects', { signal });
  return body.prospects;
}

export async function getProspect(id: number, signal?: AbortSignal): Promise<Prospect> {
  const body = await request<{ prospect: Prospect }>(`/api/prospects/${id}`, { signal });
  return body.prospect;
}

export async function saveProspect(placeId: string, sectorId?: string): Promise<Prospect> {
  const body = await request<{ prospect: Prospect }>('/api/prospects', {
    method: 'POST',
    body: { placeId, ...(sectorId ? { sectorId } : {}) },
  });
  return body.prospect;
}

export async function updateProspect(id: number, patch: ProspectPatch): Promise<Prospect> {
  const body = await request<{ prospect: Prospect }>(`/api/prospects/${id}`, {
    method: 'PATCH',
    body: patch,
  });
  return body.prospect;
}

/**
 * Vide la bibliotheque entiere. Les fiches sont **oubliees** : elles
 * reapparaitront dans les recherches sans annotation « Deja traite ».
 *
 * Irreversible : n'appeler qu'apres confirmation explicite de l'utilisateur.
 */
export async function clearLibrary(): Promise<number> {
  const body = await request<{ removed: number }>('/api/prospects?confirm=vider', {
    method: 'DELETE',
  });
  return body.removed;
}

/** Retire de la bibliotheque et memorise l'entreprise comme deja traitee. */
export async function removeProspect(id: number): Promise<string> {
  const body = await request<{ placeId: string }>(`/api/prospects/${id}`, {
    method: 'DELETE',
  });
  return body.placeId;
}

export async function setWebsiteQuality(
  id: number,
  quality: WebsiteQuality,
): Promise<Prospect> {
  const body = await request<{ prospect: Prospect }>(`/api/prospects/${id}/website-quality`, {
    method: 'POST',
    body: { quality },
  });
  return body.prospect;
}

/** Un clic = au maximum un appel Place Details. */
export async function refreshProspect(id: number): Promise<Prospect> {
  const body = await request<{ prospect: Prospect }>(`/api/prospects/${id}/refresh`, {
    method: 'POST',
    body: {},
  });
  return body.prospect;
}

/** Un clic = au maximum une analyse PageSpeed. */
export async function analyzeProspect(id: number, force = false): Promise<Prospect> {
  const body = await request<{ prospect: Prospect }>(`/api/prospects/${id}/pagespeed`, {
    method: 'POST',
    body: { force },
  });
  return body.prospect;
}

export interface LibraryImportResult {
  added: number;
  updated: number;
  skipped: number;
  ignored: number;
  treated: number;
  websiteReviews: number;
}

/** Recupere l'export brut pour le proposer au telechargement. */
export async function exportLibraryFile(): Promise<{ content: string; fileName: string }> {
  const auth = await authHeaders();
  let response: Response;

  try {
    response = await fetch(apiUrl('/api/library/export'), { headers: auth });
  } catch {
    throw new ApiError('NETWORK_ERROR', GENERIC_MESSAGE);
  }

  if (!response.ok) {
    throw new ApiError('EXPORT_FAILED', "L'export de la bibliotheque a echoue.");
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = disposition.match(/filename="([^"]+)"/);

  return {
    content: await response.text(),
    fileName: match?.[1] ?? 'prospects-bibliotheque.json',
  };
}

export async function importLibraryFile(
  content: string,
  overwrite: boolean,
): Promise<LibraryImportResult> {
  let payload: unknown;
  try {
    payload = JSON.parse(content);
  } catch {
    throw new ApiError('INVALID_FILE', "Ce fichier n'est pas un export lisible.");
  }

  const body = await request<{ result: LibraryImportResult }>(
    `/api/library/import?overwrite=${overwrite ? 'true' : 'false'}`,
    { method: 'POST', body: payload },
  );
  return body.result;
}

/**
 * Analyse le site d'une entreprise **non sauvegardee**, depuis le dossier
 * « A traiter ». Un appel = une analyse. Le serveur saute de lui-meme un
 * resultat en cache encore valide, donc rappeler ne coute rien.
 */
export async function analyzeBusiness(
  placeId: string,
  force = false,
): Promise<PagespeedSummary | null> {
  const body = await request<{ pagespeed: PagespeedSummary | null }>(
    `/api/businesses/${encodeURIComponent(placeId)}/pagespeed`,
    { method: 'POST', body: { force } },
  );
  return body.pagespeed;
}

/** Jugement humain sur le site d'une entreprise non sauvegardee. */
export function setBusinessWebsiteQuality(
  placeId: string,
  quality: WebsiteQuality,
): Promise<unknown> {
  return request(`/api/businesses/${encodeURIComponent(placeId)}/website-quality`, {
    method: 'POST',
    body: { quality },
  });
}

export function ignoreBusiness(placeId: string): Promise<unknown> {
  return request('/api/ignored-businesses', { method: 'POST', body: { placeId } });
}

export function unignoreBusiness(placeId: string): Promise<unknown> {
  return request(`/api/ignored-businesses/${encodeURIComponent(placeId)}`, {
    method: 'DELETE',
  });
}

/** Oublie la memoire "deja traite" pour cet etablissement. */
export function untreatBusiness(placeId: string): Promise<unknown> {
  return request(`/api/treated-businesses/${encodeURIComponent(placeId)}`, {
    method: 'DELETE',
  });
}

/** Marque directement un etablissement "deja traite", sans le sauvegarder. */
export function markBusinessTreated(placeId: string): Promise<unknown> {
  return request(`/api/businesses/${encodeURIComponent(placeId)}/treated`, {
    method: 'POST',
  });
}
